import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, ButtonBase, ClickAwayListener, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import ThumbUpOffAltOutlined from '@mui/icons-material/ThumbUpOffAltOutlined';
import ThumbDownOffAltOutlined from '@mui/icons-material/ThumbDownOffAltOutlined';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { NotificationDTO, notificationService } from '../service/notificationService';
import { emitLocalEvent } from '../../shared/realtime/ws';
import { APP_HEADER_HEIGHT_PX } from '../../shared/config/tabKeepAlive';
import { CHROME_OVERLAY_Z, getNavTokens, navFocusRing } from '../../shared/navigation/navTokens';

const MESSAGE_PREVIEW_MAX_CHARS = 180;

interface Props {
  open: boolean;
  onClose: () => void;
  companyId: string;
  userId?: string;
}

function formatWhen(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
}

export const NotificationPanel: React.FC<Props> = ({ open, onClose, companyId, userId }) => {
  const theme = useTheme();
  const t = getNavTokens(theme);
  const [items, setItems] = useState<NotificationDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    if (!companyId) { setItems([]); return; }
    setLoading(true);
    try {
      const data = await notificationService.getOpen(companyId, userId);
      // Mais nova primeiro (por created_at)
      const sorted = [...data].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
      setItems(sorted);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, userId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (!open) {
      setExpandedIds({});
    }
  }, [open]);

  const handleLike = async (id: string) => {
    await notificationService.like(id);
    // remove do painel e atualiza o sino na hora
    setItems(prev => prev.filter(x => x.id !== id));
    emitLocalEvent({ type: 'NOTIFICATION_UPDATED', company_id: companyId });
  };

  const handleDislike = async (id: string) => {
    await notificationService.dislike(id);
    setItems(prev => prev.filter(x => x.id !== id));
    emitLocalEvent({ type: 'NOTIFICATION_UPDATED', company_id: companyId });
  };

  const handleLikeAll = async () => {
    if (!companyId) return;
    try {
      await notificationService.likeAll();
      setItems([]);
      emitLocalEvent({ type: 'NOTIFICATION_UPDATED', company_id: companyId });
    } catch (error: any) {
      // 401: o interceptor já redireciona
      if (error.response?.status === 401) {
        console.warn('Não autorizado ao marcar todas como lidas');
        return;
      }
      await load();
    }
  };

  const getPreviewMessage = (message?: string) => {
    if (!message) return '';
    if (message.length <= MESSAGE_PREVIEW_MAX_CHARS) return message;
    return `${message.slice(0, MESSAGE_PREVIEW_MAX_CHARS).trimEnd()}...`;
  };

  if (!open) return null;

  const smallIconSx = {
    width: 32,
    height: 32,
    color: t.muted,
    '&:hover': { backgroundColor: t.hover, color: t.ink },
    ...navFocusRing(t),
  };

  // Fecha ao clicar fora (inclusive ao abrir outro menu, como o da conta). O sino alterna.
  return (
    <ClickAwayListener onClickAway={onClose}>
    <Paper
      elevation={0}
      role="dialog"
      aria-label="Notificações"
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: 'fixed',
        top: APP_HEADER_HEIGHT_PX + 4,
        right: 12,
        maxHeight: `calc(100dvh - ${APP_HEADER_HEIGHT_PX + 20}px)`,
        width: { xs: 'calc(100vw - 24px)', sm: 380 },
        zIndex: CHROME_OVERLAY_Z,
        backgroundColor: t.chrome,
        backgroundImage: 'none',
        borderRadius: '16px',
        boxShadow: '0 12px 32px rgba(15,23,42,0.18), 0 2px 6px rgba(15,23,42,0.08)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2.5, pr: 1, pt: 1.5, pb: 1 }}>
        <Typography component="h2" sx={{ flex: 1, fontSize: '0.9375rem', fontWeight: 600, color: t.ink }}>
          Notificações
          {items.length > 0 && (
            <Box component="span" sx={{ ml: 1, fontWeight: 400, color: t.muted }}>{items.length}</Box>
          )}
        </Typography>
        {items.length > 0 && (
          <Button
            type="button"
            size="small"
            variant="text"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleLikeAll(); }}
            sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.8125rem', color: t.accent, borderRadius: '8px' }}
          >
            Marcar todas como lidas
          </Button>
        )}
        <IconButton
          aria-label="Fechar notificações"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClose(); }}
          sx={smallIconSx}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ overflowY: 'auto', px: 1, pb: 1 }}>
        {items.map((n) => {
          const isExpanded = !!expandedIds[n.id];
          const shouldTruncate = (n.message?.length || 0) > MESSAGE_PREVIEW_MAX_CHARS;
          const displayMessage = isExpanded ? n.message : getPreviewMessage(n.message);
          const urgent = n.type === 1;
          return (
            <Box
              key={n.id}
              sx={{
                display: 'flex',
                gap: 1.5,
                px: 1.5,
                py: 1.25,
                borderRadius: '12px',
                '&:hover': { backgroundColor: t.hover },
              }}
            >
              <Box
                aria-hidden="true"
                sx={{ width: 8, height: 8, mt: '7px', borderRadius: '50%', flex: 'none', bgcolor: urgent ? '#D93025' : t.accent }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: t.ink, wordBreak: 'break-word' }}>
                  {n.title}
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: '0.8125rem', lineHeight: 1.45, color: t.muted, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {displayMessage}
                </Typography>
                {shouldTruncate && (
                  <ButtonBase
                    onClick={() => setExpandedIds((prev) => ({ ...prev, [n.id]: !prev[n.id] }))}
                    sx={{ mt: 0.5, fontSize: '0.8125rem', fontWeight: 500, color: t.accent, borderRadius: '4px', ...navFocusRing(t) }}
                  >
                    {isExpanded ? 'Ver menos' : 'Ver mais'}
                  </ButtonBase>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.5, ml: -0.75 }}>
                  <Typography component="span" sx={{ fontSize: '0.75rem', color: t.muted, px: 0.75 }}>
                    {urgent && <Box component="span" sx={{ color: '#D93025', fontWeight: 600 }}>Urgente · </Box>}
                    {formatWhen(n.created_at)}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="Útil">
                    <IconButton onClick={() => handleLike(n.id)} aria-label="Gostei" sx={smallIconSx}>
                      <ThumbUpOffAltOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Não útil">
                    <IconButton onClick={() => handleDislike(n.id)} aria-label="Não gostei" sx={smallIconSx}>
                      <ThumbDownOffAltOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          );
        })}

        {!loading && items.length === 0 && (
          <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: t.muted }}>
            <NotificationsNoneOutlined sx={{ fontSize: 32 }} />
            <Typography sx={{ fontSize: '0.875rem', color: t.muted }}>Nenhuma notificação aberta</Typography>
          </Box>
        )}
      </Box>
    </Paper>
    </ClickAwayListener>
  );
};
